import { useState } from 'react';

type Kind = 'text' | 'textarea' | 'number';

interface Props {
  label: string;
  value: string;
  kind?: Kind | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Shown instead of the edit button while disabled — why it is locked. */
  hint?: string | undefined;
  /** Whether an empty value may be saved (clears the field). */
  allowEmpty?: boolean | undefined;
  /** Unit after the value in view mode (currency). */
  suffix?: string | undefined;
  min?: number | undefined;
  step?: string | undefined;
  onSave: (value: string) => Promise<void>;
}

const EDITOR =
  'rounded border border-[var(--co-brand)] bg-[var(--co-bg)] px-1.5 py-0.5 text-[12px] text-[var(--co-text)] outline-none';

/**
 * View/edit field in the old Configs inline-field style, generalised:
 * hover reveals the pencil, Enter (single-line) or OK commits, Escape cancels,
 * blur commits. The OK button prevents the mousedown so blur does not fire a
 * second save.
 */
export function InlineTextField({
  label,
  value,
  kind = 'text',
  placeholder,
  disabled = false,
  hint,
  allowEmpty = false,
  suffix,
  min,
  step,
  onSave,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    setIsEditing(false);
    if ((next === '' && !allowEmpty) || next === value) return;
    setIsSaving(true);
    try {
      await onSave(next);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && kind !== 'textarea') void commit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-1.5">
        <span className="mt-0.5 shrink-0 text-[11px] text-[var(--co-muted)]">{label}:</span>
        {kind === 'textarea' ? (
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => void commit()}
            aria-label={label}
            {...(placeholder ? { placeholder } : {})}
            className={`min-h-[3rem] w-full resize-y ${EDITOR}`}
          />
        ) : (
          <input
            autoFocus
            type={kind === 'number' ? 'number' : 'text'}
            {...(kind === 'number' ? { inputMode: 'decimal' as const } : {})}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => void commit()}
            aria-label={label}
            {...(placeholder ? { placeholder } : {})}
            {...(min !== undefined ? { min } : {})}
            {...(step ? { step } : {})}
            className={`${kind === 'number' ? 'w-28' : 'w-64'} ${EDITOR}`}
          />
        )}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void commit()}
          className="shrink-0 rounded bg-[var(--co-brand)] px-2 py-0.5 text-[11px] font-semibold text-white"
        >
          OK
        </button>
      </div>
    );
  }

  return (
    <div className="group/inline flex items-start gap-1">
      <span className="mt-0.5 shrink-0 text-[11px] text-[var(--co-muted)]">{label}:</span>
      <span
        className={`text-[12px] leading-snug ${value ? 'text-[var(--co-text)]' : 'text-[var(--co-muted)]'} ${
          kind === 'number' ? 'font-mono' : ''
        }`}
      >
        {value ? `${value}${suffix ? ` ${suffix}` : ''}` : '—'}
      </span>
      {disabled ? (
        hint ? (
          <span className="ml-1 text-[11px] italic text-[var(--co-muted)]">{hint}</span>
        ) : null
      ) : (
        <button
          type="button"
          onClick={startEditing}
          disabled={isSaving}
          title={`Edit ${label}`}
          aria-label={`Edit ${label}`}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--co-muted)] opacity-0 transition-opacity hover:bg-[var(--co-bg)] hover:text-[var(--co-text)] focus:opacity-100 group-hover/inline:opacity-100"
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M11.1 2.2a1.4 1.4 0 0 1 2 2L5.4 11.9l-2.7.7.7-2.7 7.7-7.7Z" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
